/**
 * Safety parameter value calculation utilities.
 * Moved from operators/device-grid-settings/device-grid-settings.utils.ts.
 * These compute default vs current values from Modbus register data + SafetyMetadata.
 */
import { InverterData } from 'ngx-core-services';
import { BitFunction, InverterRegister, MultiBitFunction } from 'ngx-modbus';
import { SafetyMetadata } from '../../SGP/general.model';

import { InverterRegisterConfig } from './safety-value-utils.model';

const START_ADDRESS = 0x0800;
const BLOCK_SIZE = 64;

export const calculateParameterValuesWithUnit = (
  element: InverterRegister,
  content: { table: InverterRegisterConfig[]; values: Partial<InverterData> },
  metadata: SafetyMetadata,
): {
  isDefault: boolean;
  defaultValue: number;
  currentValue: number;
  unit: string;
  block: number;
  offset: number;
  START_ADDRESS: number;
  BLOCK_SIZE: number;
} => {
  const position = element.address - START_ADDRESS;
  const [block, offset] = [Math.floor(position / BLOCK_SIZE), position % BLOCK_SIZE];
  const defaultValueNumber = (metadata.parsedContent as any).blocks[block + 1][offset] * (element.accuracy || 1);
  const unit = element.limits?.unit || element.unit || '';
  const currentValueNumber = content.values[(element.name as keyof InverterData)] !== undefined
    ? Number(content.values[(element.name as keyof InverterData)]) : 0;

  return {
    isDefault: defaultValueNumber === currentValueNumber,
    defaultValue: defaultValueNumber,
    currentValue: currentValueNumber,
    unit,
    block,
    offset,
    START_ADDRESS,
    BLOCK_SIZE,
  };
};

export const calculateBitFnValuesWithUnit = (
  element: InverterRegister,
  bitFn: BitFunction,
  content: { table: InverterRegisterConfig[]; values: Partial<InverterData> },
  metadata: SafetyMetadata,
): {
  isDefault: boolean;
  defaultValue?: boolean;
  currentValue?: boolean;
  block: number;
  offset: number;
  START_ADDRESS: number;
  BLOCK_SIZE: number;
} => {
  const position = element.address - START_ADDRESS;
  const [block, offset] = [Math.floor(position / BLOCK_SIZE), position % BLOCK_SIZE];

  const defaultTable = {
    [element.name]: (metadata.parsedContent as any).blocks[block + 1][offset] * (element.accuracy || 1),
  };

  const defaultValueNumber = bitFn.isEnabledFn ? bitFn.isEnabledFn(defaultTable) : undefined;
  const currentValueNumber = bitFn.isEnabledFn ? bitFn.isEnabledFn(content.values) : undefined;

  return {
    isDefault: defaultValueNumber === currentValueNumber,
    defaultValue: defaultValueNumber,
    currentValue: currentValueNumber,
    block,
    offset,
    START_ADDRESS,
    BLOCK_SIZE,
  };
};

export const calculateMultiBitFnValues = (
  element: InverterRegister,
  bitFn: MultiBitFunction,
  content: { table: InverterRegisterConfig[]; values: Partial<InverterData> },
  metadata: SafetyMetadata,
): {
  isDefault: boolean;
  defaultValue?: number;
  currentValue?: number;
  block: number;
  offset: number;
  START_ADDRESS: number;
  BLOCK_SIZE: number;
} => {
  const position = element.address - START_ADDRESS;
  const [block, offset] = [Math.floor(position / BLOCK_SIZE), position % BLOCK_SIZE];

  const defaultTable = {
    [element.name]: (metadata.parsedContent as any).blocks[block + 1][offset] * (element.accuracy || 1),
  };

  const defaultValueNumber = bitFn.selectionFn ? bitFn.selectionFn(defaultTable) : undefined;
  const currentValueNumber = bitFn.selectionFn ? bitFn.selectionFn(content.values) : undefined;

  return {
    isDefault: defaultValueNumber === currentValueNumber,
    defaultValue: defaultValueNumber,
    currentValue: currentValueNumber,
    block,
    offset,
    START_ADDRESS,
    BLOCK_SIZE,
  };
};
